import type { DamageType, DeckCard } from "@/types/cards";
import type { PlayerState } from "@/types/game";
import {
  equipAttackBonus,
  equipDefenseBonus,
  equipMagicDefenseBonus,
  equipSpellDamageBonus,
} from "./Equipment";
import {
  damageTakenMultiplier,
  hasNoMagicDefense,
} from "./Status";

// 总攻击力 = 英雄攻击 + 装备加成 + 状态修饰
export function effectiveAttack(p: PlayerState): number {
  let v = p.hero.attack + equipAttackBonus(p);
  for (const st of p.statuses) v += st.modifiers?.attackBonus ?? 0;
  return Math.max(0, v);
}

export function effectiveDefense(p: PlayerState): number {
  let v = p.hero.defense + equipDefenseBonus(p);
  for (const st of p.statuses) v += st.modifiers?.defenseBonus ?? 0;
  return Math.max(0, v);
}

export function effectiveMagicDefense(p: PlayerState): number {
  if (hasNoMagicDefense(p)) return 0; // 巨剑之舞：1回合无魔御
  let v = p.hero.magicDefense + equipMagicDefenseBonus(p);
  for (const st of p.statuses) v += st.modifiers?.magicDefenseBonus ?? 0;
  return Math.max(0, v);
}

// 伤害结算核心：
//   w  = max(0, atk - max(0, def-ignoreDef)) × mult × 弱点倍率   可闪避
//   hw = atk × mult × 弱点倍率                                   (无视防御) 可闪避
//   f  = max(0, dmg - mdef) × 弱点倍率                           可虚灵
//   sf = dmg × 弱点倍率                                          (无视魔御) 可虚灵
//   ss = min(dmg, hp-1) × 弱点倍率 (仍封顶 hp-1, 不可致死)        可反射/无敌
//   mana = min(loss, mp - 1)                                     (扣蓝留1)  可反射/无敌
// ignoreDef：狼行（弓箭手距离>1 无视5防）
export interface DamageResult {
  finalDamage: number;
  killable: boolean;
}

export function computeDamage(
  type: DamageType,
  amount: number,
  attacker: PlayerState,
  target: PlayerState,
  multiplier: number = 1,
  ignoreDefense: number = 0
): DamageResult {
  const weak = damageTakenMultiplier(target); // 致命弱点：受伤翻倍
  switch (type) {
    case "w": {
      const def = Math.max(0, effectiveDefense(target) - ignoreDefense);
      // amount 为权威基础值（攻击牌打出时已含 TG 附加加成，如幽魂/血煞/恶魔）
      return {
        finalDamage: Math.max(0, amount - def) * multiplier * weak,
        killable: true,
      };
    }
    case "hw":
      return {
        finalDamage: effectiveAttack(attacker) * multiplier * weak,
        killable: true,
      };
    case "f": {
      const spellBonus = equipSpellDamageBonus(attacker);
      return {
        finalDamage:
          Math.max(0, (amount + spellBonus) - effectiveMagicDefense(target)) * weak,
        killable: true,
      };
    }
    case "sf": {
      const spellBonus = equipSpellDamageBonus(attacker);
      return { finalDamage: (amount + spellBonus) * weak, killable: true };
    }
    case "ss": {
      const cap = Math.max(0, target.hp - 1);
      const base = Math.min(amount, cap);
      return { finalDamage: Math.min(base * weak, cap), killable: false };
    }
    case "mana":
      return {
        finalDamage: Math.min(amount, Math.max(0, target.mp - 1)),
        killable: false,
      };
    default: {
      const _: never = type;
      throw new Error(`未知伤害类型: ${String(_)}`);
    }
  }
}

// 攻击牌基础伤害：PG/TG=攻击力(w)，HG=攻击力(hw)，DA=先扣防御再双倍
// 返回 PendingEffect 所需的 base 与 multiplier
export function attackCardDamage(
  card: DeckCard,
  attacker: PlayerState
): { type: DamageType; base: number; multiplier: number } {
  if (card.name.startsWith("DA")) {
    return { type: "w", base: effectiveAttack(attacker), multiplier: 2 };
  }
  return {
    type: card.damageType ?? "w",
    base: effectiveAttack(attacker),
    multiplier: 1,
  };
}

// 应用伤害到目标：返回新 hp/mp 和是否进入濒死
export function applyDamage(
  target: PlayerState,
  res: DamageResult,
  type: DamageType
): { hp: number; mp: number; isDying: boolean; isDead: boolean } {
  if (type === "mana") {
    return {
      hp: target.hp,
      mp: Math.max(0, target.mp - res.finalDamage),
      isDying: false,
      isDead: false,
    };
  }
  const newHp = Math.max(0, target.hp - res.finalDamage);
  const lethal = res.killable && newHp <= 0;
  return { hp: newHp, mp: target.mp, isDying: lethal, isDead: lethal };
}

// 恢复生命/魔法（上限截断）
export function applyHeal(
  target: PlayerState,
  hp: number,
  mp: number
): { hp: number; mp: number } {
  return {
    hp: Math.min(target.maxHp, target.hp + hp),
    mp: Math.min(target.maxMp, target.mp + mp),
  };
}
